// SpeakStore.ts
import MyAlertStore from 'Alert/MyAlertStore';
import { getRuntimeEnv, RuntimeEnvT, isBrowser } from 'utils/runtimeEnv';

type SpeakEngineT = 'androidBridge' | 'webTts' | 'none';

type SpeakFailCodeT =
  | 'NOT_IN_BROWSER'
  | 'TTS_NOT_SUPPORTED'
  | 'NO_VOICES'
  | 'NO_KOREAN_VOICE'
  | 'SPEAK_FAILED';

type SpeakResultT =
  | { ok: true; engine: SpeakEngineT; runtime: RuntimeEnvT }
  | {
      ok: false;
      engine: SpeakEngineT;
      runtime: RuntimeEnvT;
      code: SpeakFailCodeT;
      reason: string;
      helpMessage: string;
      debugMessage?: string;
    };

export default class SpeakStore {
  private alertStore: MyAlertStore | null = null;

  /**
   * AttendanceStore에서 주입: this.speakStore.bindAlertStore(this.myAlertStore)
   */
  bindAlertStore = (alertStore: MyAlertStore) => {
    this.alertStore = alertStore;
  };

  private get engine(): SpeakEngineT {
    const env = getRuntimeEnv();

    // Android WebView 네이티브 브릿지가 있으면 최우선
    if (env.hasAndroidTtsBridge) return 'androidBridge';

    // 일반 웹(TTS)
    if (env.hasWebTts) return 'webTts';

    return 'none';
  }

  stop = () => {
    if (!isBrowser()) return;

    try {
      const w = window as any;
      const engine = this.engine;

      if (engine === 'androidBridge') {
        w.AndroidTTS?.stop?.();
        return;
      }

      if (engine === 'webTts') {
        window.speechSynthesis.cancel();
      }
    } catch {
      // stop은 실패해도 UX 영향 적음 -> 무시
    }
  };

  /**
   * “환영/로그인 직후” 등에서 엔진 깨우기(조용히)
   * - 실패해도 alert 안 띄움
   */
  warmup = async (): Promise<SpeakResultT> => {
    return this.speakInternal(' ', { strictKoreanVoice: false, silent: true });
  };

  /**
   * 일반 발화 (실패해도 조용히 result만 리턴)
   */
  speak = async (text: string): Promise<SpeakResultT> => {
    return this.speakInternal(text, { strictKoreanVoice: false, silent: true });
  };

  /**
   * “소리 테스트” 버튼용
   * - 실패하면 SpeakStore 내부에서 환경 맞춤 안내 + alert까지 처리
   */
  testSound = async (): Promise<boolean> => {
    const result = await this.speakInternal('안녕하세요. 소리 테스트입니다.', {
      strictKoreanVoice: true,
      silent: false,
    });

    if (!result.ok) {
      await this.alertStore?.customAlert(
        '소리 테스트 실패',
        `사유: ${result.reason}\n\n${result.helpMessage}\n\n[디버그]\n${
          result.debugMessage ?? ''
        }`,
        '닫기',
      );
      return false;
    }

    return true;
  };

  // ---------------- internal ----------------

  private speakInternal = async (
    text: string,
    opts: { strictKoreanVoice: boolean; silent: boolean },
  ): Promise<SpeakResultT> => {
    const runtime = getRuntimeEnv();
    const engine = this.engine;

    // warmup에서 ' ' 쓰므로, 완전 빈 문자열만 noop
    const trimmed = (text ?? '').toString().trim();
    if (!trimmed && text !== ' ') {
      return { ok: true, engine, runtime };
    }

    if (!isBrowser()) {
      return this.fail(
        engine,
        runtime,
        'NOT_IN_BROWSER',
        '브라우저 환경이 아닙니다.',
      );
    }

    try {
      // 1) Android WebView Bridge
      if (engine === 'androidBridge') {
        (window as any).AndroidTTS.speak(text);
        return { ok: true, engine, runtime };
      }

      // 2) Web SpeechSynthesis
      if (engine === 'webTts') {
        window.speechSynthesis.cancel();

        const voices = await this.getVoicesWithRetry(3, 300);

        if (voices.length === 0) {
          return this.fail(
            engine,
            runtime,
            'NO_VOICES',
            '음성 목록을 불러오지 못했습니다.',
          );
        }

        const koVoices = voices.filter((v) =>
          (v.lang || '').toLowerCase().startsWith('ko'),
        );

        if (opts.strictKoreanVoice && koVoices.length === 0) {
          return this.fail(
            engine,
            runtime,
            'NO_KOREAN_VOICE',
            '한국어 TTS 음성이 설치되어 있지 않습니다.',
          );
        }

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'ko-KR';

        // 유나 우선 -> 한국어 첫 보이스 -> 브라우저 기본
        const koYuna =
          koVoices.find((v) => v.name?.toLowerCase().includes('yuna')) ||
          koVoices.find((v) => v.name?.toLowerCase().includes('유나'));

        if (koYuna) utterance.voice = koYuna;
        else if (koVoices[0]) utterance.voice = koVoices[0];

        window.speechSynthesis.speak(utterance);
        return { ok: true, engine, runtime };
      }

      // 3) Unsupported
      return this.fail(
        engine,
        runtime,
        'TTS_NOT_SUPPORTED',
        '이 환경/브라우저에서 TTS를 지원하지 않습니다.',
      );
    } catch (error) {
      return this.fail(
        engine,
        runtime,
        'SPEAK_FAILED',
        'TTS 실행 중 오류가 발생했습니다.',
        error,
      );
    }
  };

  private getVoicesWithRetry = async (maxAttempts: number, delayMs: number) => {
    // getVoices()가 처음엔 비어있는 경우가 있어 retry
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) return voices;

      await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
    }

    return window.speechSynthesis.getVoices();
  };

  private fail = (
    engine: SpeakEngineT,
    runtime: RuntimeEnvT,
    code: SpeakFailCodeT,
    reason: string,
    error?: unknown,
  ): SpeakResultT => {
    const helpMessage = this.buildHelpMessage(engine, runtime, code);
    const debugMessage = this.buildDebugMessage(engine, runtime, code, error);

    // silent 옵션은 speakInternal에서 정책으로 사용하지만,
    // 현재는 “silent라도 결과는 리턴”만 하고 alert는 testSound에서만 띄움.
    return {
      ok: false,
      engine,
      runtime,
      code,
      reason,
      helpMessage,
      debugMessage,
    };
  };

  private buildDebugMessage = (
    engine: SpeakEngineT,
    runtime: RuntimeEnvT,
    code: SpeakFailCodeT,
    error?: unknown,
  ) => {
    const err =
      error instanceof Error
        ? `${error.name}: ${error.message}`
        : error
        ? String(error)
        : '';

    return [
      `engine=${engine}`,
      `code=${code}`,
      `os=${runtime.os}`,
      `standalone=${runtime.isStandalone}`,
      `nativeAndroid=${runtime.isNativeAndroidApp}`,
      `bridge=${runtime.hasAndroidTtsBridge}`,
      `webTts=${runtime.hasWebTts}`,
      `ua=${runtime.userAgent}`,
      err ? `error=${err}` : '',
    ]
      .filter(Boolean)
      .join('\n');
  };

  private buildHelpMessage = (
    engine: SpeakEngineT,
    runtime: RuntimeEnvT,
    code: SpeakFailCodeT,
  ) => {
    // 실행 형태(앱/홈화면/탭) 안내
    const runModeHelp = (() => {
      if (runtime.os === 'android') {
        if (runtime.isNativeAndroidApp) {
          return '현재: 안드로이드 네이티브 앱(WebView)로 실행 중입니다.';
        }
        if (runtime.isStandalone) {
          return '현재: 안드로이드 홈화면(PWA)으로 실행 중입니다.';
        }
        return '현재: 안드로이드 “브라우저 탭”에서 실행 중입니다. 홈화면에 설치(PWA) 후 실행해 주세요.';
      }

      if (runtime.os === 'ios') {
        if (runtime.isStandalone) {
          return '현재: iOS 홈화면(PWA)으로 실행 중입니다.';
        }
        return '현재: iOS “브라우저 탭”에서 실행 중입니다. 홈화면에 설치(PWA) 후 실행해 주세요.';
      }

      return '현재: PC/기타 환경으로 실행 중입니다. 출결앱은 모바일 홈화면(PWA) 또는 안드로이드 네이티브 앱을 권장합니다.';
    })();

    // TTS 설정 안내 (환경별)
    const ttsSetupHelp = (() => {
      const androidTtsHelp =
        '안드로이드 TTS 설정: 설정 > 접근성(또는 일반 관리) > 텍스트 음성 변환(TTS)에서 선호 엔진(삼성 TTS 또는 Google TTS)을 선택한 뒤, 한국어 음성 데이터를 다운로드하세요. (기기마다 메뉴명이 다를 수 있습니다).';

      const androidYunaHelp =
        '“유나” 음성이 필요하면: 설정 > 접근성(또는 일반 관리) > 텍스트 음성 변환(TTS) > 선호 엔진(삼성 TTS) > 톱니바퀴 > 한국어 음성 데이터 다운로드 > 유나 선택.';

      const iosHelp =
        'iOS TTS 설정: 설정 > 손쉬운 사용(접근성) > 음성 콘텐츠(또는 말하기)에서 한국어 음성을 다운로드/활성화한 뒤 다시 시도해 주세요. 무음 모드/볼륨도 확인해 주세요.';

      if (runtime.os === 'android') {
        return engine === 'androidBridge'
          ? `${androidTtsHelp}\n${androidYunaHelp}`
          : androidTtsHelp;
      }

      if (runtime.os === 'ios') return iosHelp;

      return '지원되는 모바일 환경(안드로이드/iOS)에서 홈화면 설치(PWA) 또는 네이티브 앱으로 실행해 주세요.';
    })();

    // 코드별로 문구를 조금 더 구체화
    if (code === 'TTS_NOT_SUPPORTED') {
      return `${runModeHelp}\n\n현재 브라우저에서 음성 안내 기능을 지원하지 않습니다.\n${ttsSetupHelp}`;
    }

    if (code === 'NO_VOICES' || code === 'NO_KOREAN_VOICE') {
      return `${runModeHelp}\n\n한국어 음성 데이터를 설치/활성화한 뒤 다시 시도해 주세요.\n${ttsSetupHelp}`;
    }

    if (code === 'SPEAK_FAILED') {
      return `${runModeHelp}\n\n일시적인 오류일 수 있습니다. 기기 볼륨/무음 모드 확인 후 다시 시도해 주세요.\n${ttsSetupHelp}`;
    }

    // NOT_IN_BROWSER 등
    return `${runModeHelp}\n\n${ttsSetupHelp}`;
  };
}
